/** Shared helpers for service-agreement tariff edit/create modals. */

export const toIsoDateValue = (value: string | Date | null | undefined): string | null => {
  if (value == null || value === '') return null
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
  return new Date(value).toISOString().split('T')[0] ?? null
}

export interface RankableCcaTariff {
  schedule: string
  name: string | null
  utilityShortName?: string | null
  utilityName?: string | null
}

/** Prefer CCA schedules that equal or prefix-match the current IOU schedule. */
export function rankCcaTariffs<T extends RankableCcaTariff>(tariffs: T[], iouSchedule: string): T[] {
  if (!iouSchedule) return [...tariffs]
  const scored = tariffs.map((t, index) => {
    let score = 0
    if (t.schedule === iouSchedule) score = 2
    else if (t.schedule.startsWith(iouSchedule)) score = 1
    return { t, score, index }
  })
  scored.sort((a, b) => b.score - a.score || a.index - b.index)
  return scored.map((s) => s.t)
}

export interface CcaTariffGroup<T extends RankableCcaTariff> {
  label: string
  tariffs: T[]
}

/**
 * Group CCA/DA tariffs by provider name for optgroup rendering.
 * Preferred provider (e.g. current assignment) sorts first; then A–Z.
 * Within each group, tariffs are ranked against the IOU schedule.
 */
export function groupCcaTariffsByUtility<T extends RankableCcaTariff>(
  tariffs: T[],
  preferredShortName: string | null | undefined,
  iouSchedule = ''
): CcaTariffGroup<T>[] {
  const byLabel = new Map<string, { shortName: string | null; items: T[] }>()
  for (const t of tariffs) {
    const label = t.utilityName?.trim() || t.utilityShortName?.trim() || 'Other'
    const existing = byLabel.get(label)
    if (existing) {
      existing.items.push(t)
    } else {
      byLabel.set(label, { shortName: t.utilityShortName ?? null, items: [t] })
    }
  }

  const preferred = preferredShortName?.trim() || null
  const groups = [...byLabel.entries()].map(([label, { shortName, items }]) => ({
    label,
    shortName,
    tariffs: rankCcaTariffs(items, iouSchedule),
  }))

  groups.sort((a, b) => {
    const aPref = preferred != null && a.shortName === preferred ? 0 : 1
    const bPref = preferred != null && b.shortName === preferred ? 0 : 1
    if (aPref !== bPref) return aPref - bPref
    return a.label.localeCompare(b.label)
  })

  return groups.map(({ label, tariffs: groupTariffs }) => ({ label, tariffs: groupTariffs }))
}

export function ccaTariffOptionKey(t: RankableCcaTariff): string {
  return t.utilityShortName ? `${t.schedule}||${t.utilityShortName}` : t.schedule
}

export function ccaTariffOptionLabel(t: RankableCcaTariff): string {
  return t.name ? `${t.schedule} — ${t.name}` : t.schedule
}
